import { Injectable, inject, signal } from '@angular/core';
import { ApiService } from './api.service';
import { S3Service, UploadProgress } from './s3.service';
import {
  SiteSettingsDTO,
  UpdateSiteSettingsDTO,
  LogoUploadRequestDTO,
  LogoUploadResponseDTO,
} from 'portal-shared';

@Injectable({
  providedIn: 'root',
})
export class SiteSettingsService {
  private apiService = inject(ApiService);
  private s3Service = inject(S3Service);

  // Cached site settings
  private readonly cachedSettings = signal<SiteSettingsDTO | null | undefined>(
    undefined,
  );
  readonly settings = this.cachedSettings.asReadonly();

  async getSiteSettings(): Promise<SiteSettingsDTO | null> {
    // Return cached value if available (undefined means not yet loaded)
    const cached = this.cachedSettings();
    if (cached !== undefined) {
      return cached;
    }

    // Fetch and cache
    try {
      const settings = await this.apiService.get<SiteSettingsDTO | undefined>(
        '/sites/settings',
      );
      const result = settings ?? null;
      this.cachedSettings.set(result);
      return result;
    } catch (error) {
      // Handle 404 as null (no settings exist yet)
      const httpError = error as {
        status?: number;
        response?: { status?: number };
      };
      if (httpError?.status === 404 || httpError?.response?.status === 404) {
        this.cachedSettings.set(null);
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  }

  // Clear the cache to force a fresh fetch on next getSiteSettings() call
  clearCache(): void {
    this.cachedSettings.set(undefined);
  }

  // Site settings methods (previously in SiteSettingsService)
  async updateSiteSettings(
    updates: UpdateSiteSettingsDTO,
  ): Promise<SiteSettingsDTO> {
    const result = await this.apiService.put<SiteSettingsDTO | undefined>(
      '/sites/settings',
      updates,
    );
    if (!result) {
      throw new Error(
        'Failed to update site settings: No response from server',
      );
    }
    // Don't cache here - caller should clear cache and reload to get fresh data
    return result;
  }

  async generateLogoUploadUrl(
    request: LogoUploadRequestDTO,
  ): Promise<LogoUploadResponseDTO> {
    const response = await this.apiService.post<
      LogoUploadResponseDTO | undefined
    >('/sites/logo/upload-url', request);
    if (!response) {
      throw new Error(
        'Failed to generate logo upload URL: No response from server',
      );
    }
    return response;
  }

  async uploadFileToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<void> {
    return this.s3Service.uploadFileToS3(uploadUrl, file, onProgress);
  }

  async deleteSiteSettings(): Promise<void> {
    await this.apiService.delete('/sites/settings');
  }
}
